<script setup lang="ts">
import type { SiteSectionCategoryId, SiteSectionId, V4HSiteId } from "@/data/common/ids";
import type { DateRange } from "@/domains/v4b/site-consumption/site-consumption.types";

import { computed } from "vue";

import { useDashboardMonoController } from "@/app/data/dashboard/dashboard-mono.controller";
import { EDashboardType } from "@/app/data/dashboard/dashboard.types";
import { useDashboardMonositeQueries } from "@/composables/dashboard/use-dashboard-monosite-queries";
import { useFeatureFlag } from "@/composables/feature-flag/use-feature-flag";
import { DATA_TEST_IDS } from "@/constants/data-test-ids/data-test-ids.constant";
import { keyOf, useTypedIntl } from "@/locales/typed-intl";
import { ERouteName } from "@/router/router.type";
import { newSectionManagementFeatureFlag } from "@/services/feature-flags/env-feature-flags";
import $date from "@/utils/date";

import AreaConsumptionChart from "@/app/data/dashboard/cards/common/area-consumption-chart.vue";
import HospitalityButton from "@/components/ui/buttons/hospitality-button.vue";

interface IProps {
  fromDay: Date;
  toDay: Date;
  siteId: V4HSiteId;
  selectedSectionIds?: Array<SiteSectionId>;
  selectedSectionCategoryIds?: Array<SiteSectionCategoryId>;
}

const props = defineProps<IProps>();

const MONO = 1;
const { t, k } = useTypedIntl();
const { isEnvFeatureEnabled } = useFeatureFlag();

const computedSiteId = computed(() => props.siteId);
const dateRange = computed<DateRange>(() => {
  return {
    from: $date.formatLocalDate(props.fromDay),
    to: $date.formatLocalDate(props.toDay),
  };
});
const computedSelectedSectionIds = computed<Array<SiteSectionId>>(() => {
  if (!props.selectedSectionIds) return [];
  return props.selectedSectionIds;
});
const computedSelectedSectionCategoryIds = computed<Array<SiteSectionCategoryId>>(() => {
  if (!props.selectedSectionCategoryIds) return [];
  return props.selectedSectionCategoryIds;
});

const { siteConsumption, missingInfos, isSiteConsumptionPending } = useDashboardMonositeQueries(
  computedSiteId,
  dateRange,
  computedSelectedSectionIds,
  computedSelectedSectionCategoryIds,
);

const { formattedConsumptionAreaBySectionSeries, isMissingPartialSectionSurfaceArea, isMissingAllSectionSurfaceArea } =
  useDashboardMonoController(siteConsumption, missingInfos, undefined);
</script>

<template>
  <AreaConsumptionChart
    :data-test-id="DATA_TEST_IDS.views.dashboard.charts.consumptionPerM2Chart"
    :has-no-data="isMissingAllSectionSurfaceArea"
    :is-loading="isSiteConsumptionPending"
    :series="formattedConsumptionAreaBySectionSeries"
    :type="EDashboardType.MONOSITE"
  >
    <template #header-right>
      <router-link
        v-if="isMissingPartialSectionSurfaceArea"
        class="flex gap-2 items-center bg-warning-soft text-warning-700 px-1 cursor-pointer rounded-full"
        target="_blank"
        :to="{
          name: isEnvFeatureEnabled(newSectionManagementFeatureFlag)
            ? ERouteName.SITE_SECTIONS_TREE
            : ERouteName.SITE_SECTIONS,
          params: { siteId: props.siteId },
        }"
      >
        <i class="pi pi-info-circle text-xs" />
        <small>{{ t(k.data.dashboard.insights.m2Consumption.missingSurfaceArea({ count: MONO })) }}</small>
      </router-link>
    </template>

    <template #noDataMessage>
      <div class="flex flex-col gap-4">
        <h5>{{ t(k.data.dashboard.noDataChartMessage.title) }}</h5>
        <i18n-t
          class="whitespace-pre-line"
          :keypath="keyOf(k.data.dashboard.missingSurfaceArea.subtitle.total[EDashboardType.MONOSITE])"
          tag="p"
        >
          <template #sitesManagementTab>
            <router-link
              :data-test-id="DATA_TEST_IDS.views.dashboard.links.sitesManagementLink"
              target="_blank"
              :to="{
                name: isEnvFeatureEnabled(newSectionManagementFeatureFlag)
                  ? ERouteName.SITE_SECTIONS_TREE
                  : ERouteName.SITE_SECTIONS,
                params: { siteId },
              }"
            >
              <HospitalityButton
                severity="link"
                size="xs"
              >
                {{ t(k.components.layout.sideMenu.sitesManagement({ count: MONO })) }}</HospitalityButton
              >
            </router-link>
          </template>
        </i18n-t>
      </div>
    </template>
  </AreaConsumptionChart>
</template>
